
import { defineComponent, onUpdated, Ref, ref, toRefs } from "vue";
import Panel from "./Panel.vue";
import ValuePreview from "./ValuePreview.vue";
import ValueGroupPreview from "./ValueGroupPreview.vue";
import useToolbar from "@/composables/useToolbar";
import { factors, closest, multiples, closestToZero } from "@/utils";

// TODOs:
// 1. handle gutter solutions!
// correct them if gutter is not a multiple of leading (separately... maybe watch when leading changes and update gutter to follow (use previous value to determine next value))
// if results (gridOptions.grid) have gutter === to grid.gutter then just show row count message,
// if row count is === to grid.rows then just show gutter message.
// 4. adapt unit to current selected unit
// 5. animate page preview changes?

export default defineComponent({
  name: "ToolbarFixes",
  components: { Panel, ValuePreview, ValueGroupPreview },
  setup() {
    const root = ref(null) as Ref<HTMLElement | null>;
    onUpdated(() => {
      root.value?.scrollTop;
    });

    const { global, dimensions, margins, grid, isPassing } = useToolbar();

    const { checkGrid, robotNote } = toRefs(global);
    const {
      baseline: baselinePassing,
      grid: gridPassing,
      all: allPassing
    } = toRefs(isPassing);

    const leadingMultiples = multiples(global.leading, dimensions.heightPt);
    const marginMatch = closest(leadingMultiples, margins.safe);
    const rowSizeMatch = closest(leadingMultiples, grid.rowSize);

    // 1. define three arrays for all possible values of: rowCount, rowSize, gutter
    const possible = [
      [...Array(Math.ceil(margins.lines / 2)).keys()].slice(2), // rowCount
      multiples(global.leading, margins.safe / 2), // rowSize
      multiples(global.leading, margins.safe / 2) // gutterSize
    ];

    // 2. find all combos of those three arrays
    // don't fully understand this (classic) but it returns an array of combos—just what i need
    // https://stackoverflow.com/a/44338759/8703073
    function* cartesian(
      head?: number[],
      ...tail: number[][]
    ): object | number[][] {
      const remainder: object | number[][] = tail.length
        ? cartesian(...tail)
        : [[]];
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      for (const r of remainder) for (const h of head) yield [h, ...r];
    }

    // 3. filter combos to only those that when test()'d fall within margins.safe
    const test = (rowCount: number, rowSize: number, gutter: number) =>
      rowCount * rowSize + (rowCount - 1) * gutter;

    const combos = [...cartesian(...possible)]
      .filter(combo => test(combo[0], combo[1], combo[2]) === margins.safe)
      .filter(
        combo => combo[2] <= combo[1] // removes combos where gutter is larger than rowsize
      )
      .filter(
        combo => combo[2] <= global.leading * 4 // removes combos where gutter is larger 4 times the leading
      );

    const baselineOptions: { margins: number[]; leading: number[] } = {
      margins: [
        // the two multiples of the leading that are closest to margin safe area
        ...Object.values(marginMatch).map(i => i - margins.safe)
      ].sort(closestToZero),
      leading: [
        // the two factors of margin safe area that are closest to the leading
        ...Object.values(closest(factors(margins.safe), global.leading))
      ].sort(closestToZero)
    };

    const gridOptions: {
      margins: number[];
      grid: { value: number }[][];
    } = {
      margins: [
        // the two multiples of the rowSize that are closest to margin safe area
        ...Object.values(rowSizeMatch).map(
          i => grid.rows * i + (grid.rows - 1) * grid.gutter - margins.safe
        )
      ].sort(closestToZero),
      grid: [
        ...combos.map(combo => [
          {
            property: "gutter",
            value: combo[2],
            unit: "points",
            label: "gutter",
            actual: grid.gutter
          },
          {
            property: "rows",
            value: combo[0],
            unit: "",
            label: "row count",
            actual: grid.rows
          }
        ])
      ].sort(
        // sort by row count closest to current value (grid.rows) aka, the original request by the designer
        // TODO: also sort by closet gutter value (a[2].value/b[2].value)
        (a, b) =>
          Math.abs(grid.rows - a[1].value) - Math.abs(grid.rows - b[1].value)
      )
    };

    return {
      root,
      baselinePassing,
      gridPassing,
      allPassing,
      checkGrid,
      baselineOptions,
      gridOptions,
      robotNote
    };
  }
});
